import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const LABEL = {
  connector_count: "Sources", monthly_signal_limit: "Signals a month",
  monthly_product_event_limit: "Product events a month", user_count: "Seats",
  audit_retention_days: "Audit history (days)",
};
const FEATURE = {
  ai_assessment_enabled: "AI assessment", ai_drafting_enabled: "AI reply drafting",
  action_proposal_enabled: "Action proposals", action_approval_enabled: "Approvals",
  knowledge_enabled: "Knowledge base", live_connectors_enabled: "Live connectors",
  premium_integrations: "Premium integrations", writeback_enabled: "Status write-back",
  profile_lookup_enabled: "Customer profile lookup",
  policy_rules_enabled: "Policy rules",
};
const METRIC_LIMIT = {
  signals_ingested: "monthly_signal_limit",
  product_events_ingested: "monthly_product_event_limit",
};

const TERM_LABEL = {
  pilot_5w: "5-week pilot", monthly: "billed monthly", quarterly: "billed every 3 months",
  half_yearly: "billed every 6 months", yearly: "billed yearly",
};

// Where Enterprise enquiries go.
const SALES_EMAIL = "sales@prioris.app";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

function daysLeft(periodEnd) {
  if (!periodEnd) return null;
  return Math.ceil((new Date(periodEnd).getTime() - Date.now()) / 86_400_000);
}

/** What this plan allows, and how much of it is spent. */
export default function SettingsPlan() {
  const { permissions } = useAuth();
  const [ent, setEnt] = useState(null);
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api.get("/entitlements").then((r) => setEnt(r.data)).catch(() => setEnt(false));
    api.get("/usage").then((r) => setUsage(r.data)).catch(() => {});
    api.get("/plans").then((r) => setPlans(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  if (ent === null) return <div className="text-[13px]" style={{ color: "var(--text-muted)" }}>Loading plan…</div>;
  if (ent === false) return <div className="text-[13px]" style={{ color: "var(--text-muted)" }}>Could not load the plan.</div>;

  const values = ent.entitlements || {};
  const limits = Object.keys(LABEL).filter((k) => k in values);
  const features = Object.keys(FEATURE).filter((k) => k in values);
  const kinds = values.connector_kinds_allowed;
  const left = ent.billing_term === "pilot_5w" ? daysLeft(ent.period_end) : null;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <span className="badge b-wait text-[13px]" data-testid="plan-code">{ent.plan_code}</span>
        <span className="text-[12.5px]" style={{ color: "var(--text-secondary)" }}>
          {ent.status}
          {ent.billing_term && ` · ${TERM_LABEL[ent.billing_term] || ent.billing_term}`}
          {!ent.access_granted && " — access suspended"}
        </span>
        {left != null && left > 0 && (
          <span className="badge b-rev" data-testid="pilot-days-left">
            Pilot ends in {left} day{left === 1 ? "" : "s"}
          </span>
        )}
        {ent.ai_model_label && (
          <span className="text-[12.5px]" style={{ color: "var(--text-secondary)" }}
                data-testid="plan-ai-model">
            AI: {ent.ai_model_label}
          </span>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4 mb-5" data-testid="plan-limits">
        {limits.map((k) => {
          const metric = Object.keys(METRIC_LIMIT).find((m) => METRIC_LIMIT[m] === k);
          const used = usage?.metrics?.find((m) => m.metric === metric)?.value;
          return (
            <div key={k} className="card p-4 col-span-6 md:col-span-3" data-testid={`limit-${k}`}>
              <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>
                {LABEL[k]}
              </div>
              <div className="font-mono text-[20px]">
                {/* Null is unlimited, not zero and not missing. Printing "—"
                    would read as "we do not know", which is a different fact. */}
                {used != null && <span>{used.toLocaleString()} / </span>}
                {values[k] == null ? "∞" : Number(values[k]).toLocaleString()}
              </div>
            </div>
          );
        })}
      </div>

      {ent.ai_credits && (
        <AICredits credits={ent.ai_credits} pack={ent.ai_credit_pack} pilot={ent.billing_term === "pilot_5w"}
                   canBuy={permissions.includes("billing.manage")} />
      )}

      <div className="card p-5 mb-5" data-testid="plan-features">
        <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
          Included
        </div>
        <div className="grid grid-cols-12 gap-2">
          {features.map((k) => (
            <div key={k} className="col-span-12 md:col-span-6 flex items-center justify-between py-1.5"
                 style={{ borderBottom: "1px solid var(--border)" }} data-testid={`feature-${k}`}>
              <span className="text-[12.5px]">{FEATURE[k]}</span>
              <span className={`badge ${values[k] ? "b-healthy" : "b-muted"}`}>
                {values[k] ? "yes" : "no"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="card p-5 mb-5" data-testid="plan-kinds">
        <div className="text-[11px] uppercase tracking-wider mb-2" style={{ color: "var(--text-muted)" }}>
          Sources you can connect
        </div>
        <div className="text-[12.5px]" style={{ color: "var(--text-secondary)" }}>
          {kinds == null
            ? "Every kind of source."
            : (kinds || []).map((k) => k.replace(/_/g, " ")).join(", ")}
        </div>
      </div>

      {plans.length > 0 && (
        <Pricing plans={plans} current={ent.plan_code}
                 canBuy={permissions.includes("billing.manage")} />
      )}
    </div>
  );
}

/** AI credits: the plan's allowance, what is spent, and bought top-ups. */
function AICredits({ credits, pack, pilot, canBuy }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const unlimited = credits.allowance == null;
  const spent = Math.min(credits.used, credits.allowance ?? credits.used);
  const pct = unlimited || !credits.allowance ? 0 : Math.min(100, (spent / credits.allowance) * 100);

  const buy = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/billing/credits/checkout");
      window.location.assign(data.checkout_url);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not start the payment");
      setBusy(false);
    }
  };

  return (
    <div className="card p-5 mb-5" data-testid="ai-credits">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
        <div className="text-[11px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
          AI credits {pilot ? "for this pilot" : "this month"}
        </div>
        <div className="font-mono text-[13px]" data-testid="ai-credits-used">
          {unlimited ? "Unlimited"
            : `${spent.toLocaleString("en-IN")} / ${credits.allowance.toLocaleString("en-IN")} used`}
        </div>
      </div>
      {!unlimited && (
        <div className="h-1.5 rounded-full mb-3" style={{ background: "var(--overlay-2)" }}
             role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}
             aria-label="AI credits used">
          <div className="h-1.5 rounded-full"
               style={{ width: `${pct}%`,
                        background: pct >= 80 ? "var(--risk-rev)" : "var(--risk-healthy)" }} />
        </div>
      )}
      <div className="text-[12.5px] mb-3" style={{ color: "var(--text-secondary)" }}>
        One credit is one AI task: an assessment, a set of suggested actions, or a reply draft.
        {credits.topup_balance > 0 && (
          <span data-testid="ai-credits-topup">
            {" "}Plus {credits.topup_balance.toLocaleString("en-IN")} top-up credits, used after the allowance.
          </span>
        )}
      </div>
      {error && <div className="badge b-rev mb-3 inline-block" data-testid="credits-error">{error}</div>}
      {canBuy && pack && (
        <button type="button" className="btn" onClick={buy} disabled={busy}
                data-testid="buy-credits">
          {busy ? "Opening payment…"
            : `Buy ${pack.credits.toLocaleString("en-IN")} credits · ${inr(pack.amount_inr)}`}
        </button>
      )}
    </div>
  );
}

/**
 * The plans on offer. Buying sends the owner to Razorpay; the plan itself
 * changes when Razorpay confirms payment to the backend, not on return here.
 */
function Pricing({ plans, current, canBuy }) {
  const [terms, setTerms] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const buy = async (plan, term) => {
    setBusy(plan.code); setError("");
    try {
      const { data } = await api.post("/billing/checkout", { plan_code: plan.code, term });
      window.location.assign(data.checkout_url);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not start the payment");
      setBusy("");
    }
  };

  return (
    <div data-testid="plan-pricing">
      <div className="text-[11px] uppercase tracking-wider mb-3" style={{ color: "var(--text-muted)" }}>
        Plans
      </div>
      {error && <div className="badge b-rev mb-3 inline-block" data-testid="pricing-error">{error}</div>}
      <div className="grid grid-cols-12 gap-4">
        {plans.map((plan) => {
          const options = plan.pricing || [];
          const term = terms[plan.code] || options[0]?.term;
          const price = options.find((p) => p.term === term);
          const perMonth = price?.months ? Math.round(price.amount_inr / price.months) : null;
          // A launch offer is a charged price below the regular one; the
          // regular one is shown struck through, and savings count from it.
          const offer = price?.list_amount_inr > price?.amount_inr;
          const saving = offer
            ? Math.round((1 - price.amount_inr / price.list_amount_inr) * 100) : 0;
          const isCurrent = plan.code === current;
          return (
            <div key={plan.code} className="card p-5 col-span-12 md:col-span-4 flex flex-col"
                 data-testid={`pricing-${plan.code}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[15px] font-medium">{plan.name}</span>
                {isCurrent && <span className="badge b-healthy">current</span>}
              </div>
              {plan.ai_model_label && (
                <div className="text-[12px] mb-3" style={{ color: "var(--text-secondary)" }}>
                  AI: {plan.ai_model_label}
                </div>
              )}

              {options.length > 1 && (
                <div className="flex flex-wrap gap-1 mb-3" role="group" aria-label={`${plan.name} billing term`}>
                  {options.map((p) => (
                    <button key={p.term} type="button"
                            className={`badge ${p.term === term ? "b-wait" : "b-muted"}`}
                            aria-pressed={p.term === term}
                            data-testid={`term-${plan.code}-${p.term}`}
                            onClick={() => setTerms({ ...terms, [plan.code]: p.term })}>
                      {p.label}
                    </button>
                  ))}
                </div>
              )}

              <div className="mb-4 flex-1">
                {price ? (
                  <>
                    {offer && (
                      <span className="badge b-healthy mb-1 inline-block"
                            data-testid={`offer-${plan.code}`}>Launch offer</span>
                    )}
                    <div className="flex items-baseline gap-2">
                      {offer && (
                        <s className="font-mono text-[14px]" style={{ color: "var(--text-muted)" }}
                           data-testid={`list-price-${plan.code}`}>
                          <span className="sr-only">Regular price </span>
                          {inr(price.list_amount_inr)}
                        </s>
                      )}
                      <span className="font-mono text-[22px]" data-testid={`price-${plan.code}`}>
                        {inr(price.amount_inr)}
                      </span>
                    </div>
                    <div className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
                      {price.days ? `for ${Math.round(price.days / 7)} weeks, one-time`
                        : price.months === 1 ? "per month"
                        : `per ${price.months} months · ${inr(perMonth)}/month`}
                      {saving > 0 && ` · save ${saving}%`}
                    </div>
                  </>
                ) : (
                  <div className="text-[13px]" style={{ color: "var(--text-secondary)" }}>
                    Custom pricing for your scale.
                  </div>
                )}
              </div>

              {plan.contact_sales ? (
                <a className="btn btn-ghost text-center" href={`mailto:${SALES_EMAIL}`}
                   data-testid={`contact-${plan.code}`}>Contact sales</a>
              ) : canBuy && price ? (
                <button type="button" className="btn"
                        disabled={busy === plan.code || (isCurrent && !price.recurring)}
                        onClick={() => buy(plan, term)}
                        data-testid={`buy-${plan.code}`}>
                  {busy === plan.code ? "Opening payment…"
                    : isCurrent ? (price.recurring ? "Change term" : "Current plan")
                    : price.recurring ? "Upgrade" : "Start pilot"}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
